import React, { useState, useEffect, useMemo } from "react";
import { Star, ChevronDown, Plus, Minus } from "lucide-react";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import { CandleChart, generateCandles } from "../components/dashboard/Charts";
import { watchlist } from "../dashboardMock";

const timeframes = ["M1", "M5", "M15", "H1", "H4", "D1"];

const Trade = () => {
  const [active, setActive] = useState(watchlist[0]);
  const [tf, setTf] = useState("H1");
  const [side, setSide] = useState("BUY");
  const [volume, setVolume] = useState(0.1);
  const [tp, setTp] = useState("");
  const [sl, setSl] = useState("");

  // Regenerate candles when symbol/timeframe changes
  const candles = useMemo(
    () => generateCandles(70, active.bid, active.bid > 1000 ? active.bid * 0.001 : 0.0015),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active.symbol, tf]
  );

  // Live ticking bid/ask
  const [bid, setBid] = useState(active.bid);
  const [ask, setAsk] = useState(active.ask);
  useEffect(() => {
    setBid(active.bid);
    setAsk(active.ask);
    const t = setInterval(() => {
      setBid((b) => {
        const drift = (Math.random() - 0.5) * (active.bid > 1000 ? 2 : 0.0008);
        return Math.max(0.0001, b + drift);
      });
      setAsk((a) => {
        const drift = (Math.random() - 0.5) * (active.bid > 1000 ? 2 : 0.0008);
        return Math.max(0.0001, a + drift);
      });
    }, 1200);
    return () => clearInterval(t);
  }, [active]);

  const fmt = (v) => (v > 100 ? v.toFixed(2) : v.toFixed(4));
  const spread = ((ask - bid) * (active.bid > 100 ? 100 : 10000)).toFixed(1);

  return (
    <DashboardLayout title={active.symbol} subtitle="Trading terminal" activeKey="trade">
      <div className="grid lg:grid-cols-[280px_1fr_320px] gap-4">
        {/* Watchlist */}
        <div className="bb-card rounded-2xl p-3 max-h-[280px] lg:max-h-[680px] overflow-y-auto">
          <div className="px-3 py-2 flex items-center justify-between">
            <div className="text-[12px] uppercase tracking-wider text-white/45 font-semibold">Watchlist</div>
            <button className="text-white/40 hover:text-white">
              <Plus size={14} />
            </button>
          </div>
          <div className="space-y-0.5">
            {watchlist.map((w) => {
              const sel = w.symbol === active.symbol;
              const up = w.change >= 0;
              return (
                <button
                  key={w.symbol}
                  onClick={() => setActive(w)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-left transition-colors ${
                    sel
                      ? "bg-[#1c3df5]/15 border border-[#1c3df5]/30"
                      : "border border-transparent hover:bg-white/[0.04]"
                  }`}
                >
                  <Star
                    size={12}
                    className={sel ? "text-yellow-400 fill-yellow-400" : "text-white/25"}
                    fill={sel ? "currentColor" : "none"}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-white font-medium">{w.symbol}</div>
                    <div className="text-[10px] text-white/40 truncate">{w.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[12px] text-white tabular-nums">{w.bid}</div>
                    <div
                      className={`text-[10px] tabular-nums ${
                        up ? "text-emerald-400" : "text-rose-400"
                      }`}
                    >
                      {up ? "+" : ""}
                      {w.change}%
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Chart panel */}
        <div className="bb-card rounded-2xl p-4 lg:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/[0.05] border border-white/8 flex items-center justify-center text-[12px] font-bold text-white">
                {active.symbol.slice(0, 2)}
              </div>
              <div>
                <div className="font-display font-semibold text-[17px] text-white">{active.symbol}</div>
                <div className="text-[11px] text-white/45">{active.name}</div>
              </div>
              <div className="ml-4 flex items-baseline gap-3">
                <div className="font-display font-bold text-[24px] text-white tabular-nums">
                  {fmt(bid)}
                </div>
                <div
                  className={`text-[12px] font-medium ${
                    active.change >= 0 ? "text-emerald-400" : "text-rose-400"
                  }`}
                >
                  {active.change >= 0 ? "+" : ""}
                  {active.change}%
                </div>
              </div>
            </div>
            <div className="flex gap-1 bg-white/[0.04] rounded-lg p-1 border border-white/8">
              {timeframes.map((t) => (
                <button
                  key={t}
                  onClick={() => setTf(t)}
                  className={`px-2.5 py-1 text-[11px] rounded-md transition-colors ${
                    tf === t
                      ? "bg-white text-[#07080d] font-medium"
                      : "text-white/55 hover:text-white"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <CandleChart candles={candles} height={typeof window !== "undefined" && window.innerWidth < 768 ? 280 : 420} />
          <div className="mt-3 flex items-center justify-between text-[11px] text-white/40 px-1">
            <div className="flex gap-4">
              <span>O: <span className="text-white/70 tabular-nums">{fmt(candles[candles.length - 1].open)}</span></span>
              <span>H: <span className="text-emerald-400 tabular-nums">{fmt(candles[candles.length - 1].high)}</span></span>
              <span>L: <span className="text-rose-400 tabular-nums">{fmt(candles[candles.length - 1].low)}</span></span>
              <span>C: <span className="text-white/70 tabular-nums">{fmt(candles[candles.length - 1].close)}</span></span>
            </div>
            <div>Spread: <span className="text-[#7d8eff] tabular-nums">{spread}</span></div>
          </div>
        </div>

        {/* Order ticket */}
        <div className="bb-card rounded-2xl p-5">
          <div className="text-[12px] uppercase tracking-wider text-white/45 font-semibold mb-4">
            New order
          </div>

          {/* Buy / Sell tabs */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              onClick={() => setSide("SELL")}
              className={`py-3 rounded-xl border transition-all ${
                side === "SELL"
                  ? "border-rose-500 bg-rose-500/15"
                  : "border-white/8 hover:border-white/20"
              }`}
            >
              <div className="text-[10px] uppercase tracking-wider text-rose-400 font-semibold">Sell</div>
              <div className="font-display font-bold text-[20px] text-white tabular-nums">{fmt(bid)}</div>
            </button>
            <button
              onClick={() => setSide("BUY")}
              className={`py-3 rounded-xl border transition-all ${
                side === "BUY"
                  ? "border-emerald-500 bg-emerald-500/15"
                  : "border-white/8 hover:border-white/20"
              }`}
            >
              <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold">Buy</div>
              <div className="font-display font-bold text-[20px] text-white tabular-nums">{fmt(ask)}</div>
            </button>
          </div>

          {/* Order type */}
          <div className="mb-3">
            <div className="text-[11px] text-white/55 mb-1.5">Order type</div>
            <button className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-white/8 bg-white/[0.02] text-[13px] text-white">
              Market execution
              <ChevronDown size={14} className="text-white/40" />
            </button>
          </div>

          {/* Volume */}
          <div className="mb-3">
            <div className="text-[11px] text-white/55 mb-1.5">Volume (lots)</div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setVolume((v) => Math.max(0.01, +(v - 0.1).toFixed(2)))}
                className="w-9 h-9 rounded-lg border border-white/8 text-white/60 hover:text-white hover:bg-white/[0.05] flex items-center justify-center"
              >
                <Minus size={14} />
              </button>
              <input
                type="number"
                step="0.01"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value) || 0)}
                className="flex-1 h-9 bg-white/[0.02] border border-white/8 rounded-lg px-3 text-center text-[14px] text-white tabular-nums outline-none focus:border-[#1c3df5]"
              />
              <button
                onClick={() => setVolume((v) => +(v + 0.1).toFixed(2))}
                className="w-9 h-9 rounded-lg border border-white/8 text-white/60 hover:text-white hover:bg-white/[0.05] flex items-center justify-center"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          {/* SL / TP */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div>
              <div className="text-[11px] text-white/55 mb-1.5">Stop Loss</div>
              <input
                value={sl}
                onChange={(e) => setSl(e.target.value)}
                placeholder="Not set"
                className="w-full h-9 bg-white/[0.02] border border-white/8 rounded-lg px-3 text-[13px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
              />
            </div>
            <div>
              <div className="text-[11px] text-white/55 mb-1.5">Take Profit</div>
              <input
                value={tp}
                onChange={(e) => setTp(e.target.value)}
                placeholder="Not set"
                className="w-full h-9 bg-white/[0.02] border border-white/8 rounded-lg px-3 text-[13px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
              />
            </div>
          </div>

          <div className="flex justify-between text-[12px] mb-4 pb-4 border-b border-white/5">
            <span className="text-white/45">Required margin</span>
            <span className="text-white tabular-nums">
              ${(volume * bid * 100 / 500).toFixed(2)}
            </span>
          </div>

          <button
            onClick={() =>
              alert(`Order placed (demo): ${side} ${volume} ${active.symbol} @ ${side === "BUY" ? fmt(ask) : fmt(bid)}`)
            }
            className={`w-full py-3 rounded-xl font-semibold text-white transition-all ${
              side === "BUY"
                ? "bg-emerald-500 hover:bg-emerald-600"
                : "bg-rose-500 hover:bg-rose-600"
            }`}
          >
            Place {side} order
          </button>
          <div className="text-[10px] text-white/40 text-center mt-3">
            Demo mode · No real money involved
          </div>
        </div>
      </div>

    </DashboardLayout>
  );
};

export default Trade;
